<template>
  <div id="style_tab" class="my-6">
    <NotificationPopup
      v-if="widget.edited"
      status="warning"
      title="Changes Unpublished"
      text="To view your updates on the widget, please save and publish them."
      class="mb-6"
    />

    <div v-if="widget.config" class="tab-wrapper-primary">
      <div class="tab-heading-primary">{{ changeCase('customise design', ElementE.TITLE) }}</div>
      <div class="p-6">
        <div class="relative grid grid-cols-2 gap-4 pb-4">
          <SelectColor :value="widget.config?.link_style" label="Link colour" @colors="setColor($event, 'link_style')" />
          <SelectColor :value="widget.config?.link_hover_colour" label="Link hover colour" @colors="setColor($event, 'link_hover_colour')" />
          <SelectColor :value="widget.config?.button_colour" label="Button colour" @colors="setColor($event, 'button_colour')" />
          <SelectColor :value="widget.config?.button_hover_colour" label="Button hover colour" @colors="setColor($event, 'button_hover_colour')" />
          <SelectColor :value="widget.config?.button_text_colour" label="Button text colour" @colors="setColor($event, 'button_text_colour')" />
          <SelectColor :value="widget.config?.button_hover_text_colour" label="Button hover text colour" @colors="setColor($event, 'button_hover_text_colour')" />
        </div>

        <div class="grid grid-cols-2 gap-4 py-4 border-t-[1px]">
          <SelectColor :value="widget.config?.deal_btn_bg_colour" :is-change-case="false" label="“Deals for you” button colour" @colors="setColor($event, 'deal_btn_bg_colour')" />
          <SelectColor :value="widget.config?.deal_btn_text_colour" :is-change-case="false" label="“Deals for you” button text colour" @colors="setColor($event, 'deal_btn_text_colour')" />
          <SelectColor :value="widget.config?.deal_btn_hover_colour" :is-change-case="false" label="“Deals for you” button hover colour" @colors="setColor($event, 'deal_btn_hover_colour')" />
          <SelectColor :value="widget.config?.deal_btn_text_hover_colour" :is-change-case="false" label="“Deals for you” button hover text colour" @colors="setColor($event, 'deal_btn_text_hover_colour')" />
          <DropdownMenu
            :values="iconsList"
            label="“Deals for you” icon"
            :default-select="widget.config?.deal_icon_colour || 'PriceTag'"
            :is-change-case="false"
            :type="DropdownTypeE.ICON"
            @select="cnangeIcon"
          />
          <SelectColor :value="widget.config?.deal_matrix_icon_colour" :is-change-case="false" label="“Deals for you” you icon colour (in matrix-cells)" @colors="setColor($event, 'deal_matrix_icon_colour')" />
        </div>

        <div class="grid grid-cols-2 gap-4 py-4 border-t-[1px] border-dashed">
          <SelectColor :value="widget.config?.minlos_bg_colour" :is-change-case="false" label="MinLOS banner background colour" @colors="setColor($event, 'minlos_bg_colour')" />
          <SelectColor :value="widget.config?.minlos_text_colour" :is-change-case="false" label="MinLOS banner text colour" @colors="setColor($event, 'minlos_text_colour')" />
          <SelectColor :value="widget.config?.special_banner_bg_color" :is-change-case="false" label="Specials background colour" @colors="setColor($event, 'special_banner_bg_color')" />
          <SelectColor :value="widget.config?.special_banner_text" :is-change-case="false" label="Specials text colour" @colors="setColor($event, 'special_banner_text')" />
        </div>

        <div class="grid grid-cols-2 gap-4 py-4 border-t-[1px] border-dashed">
          <SelectColor :value="widget.config?.widget_card_bg_colour" :is-change-case="false" label="Widget background card colour" @colors="setColor($event, 'widget_card_bg_colour')" />
          <SelectColor :value="widget.config?.widget_card_border_colour" :is-change-case="false" label="Widget background card border colour" @colors="setColor($event, 'widget_card_border_colour')" />
        </div>

        <div class="grid grid-cols-2 gap-4 py-4 border-t-[1px] border-dashed">
          <SelectColor :value="widget.config?.property_filter_tag_colour" :is-change-case="false" label="Property filter tag colour" @colors="setColor($event, 'property_filter_tag_colour')" />
          <SelectColor :value="widget.config?.property_filter_all_tag_colour" :is-change-case="false" label="Property filter 'All properties' tag colour" @colors="setColor($event, 'property_filter_all_tag_colour')" />
          <SelectColor :value="widget.config?.property_filter_tag_text_colour" :is-change-case="false" label="Property filter tag text colour" @colors="setColor($event, 'property_filter_tag_text_colour')" />
          <SelectColor :value="widget.config?.property_filter_all_tag_text_colour" :is-change-case="false" label="Property filter 'All properties' tag text colour" @colors="setColor($event, 'property_filter_all_tag_text_colour')" />
        </div>

        <div class="w-full pt-4 border-t-[1px]">
          <div class="relative pt-2">
            <label class="label-primary flex gap-1" for="">
              Custom CSS
              <BaseTooltip
                tooltip-text="For a sample of the custom CSS code, please refer to the developer notes accessible from the menu in the upper right corner"
              >
                <template #icon>
                  <TooltipIcon />
                </template>
              </BaseTooltip>
            </label>
            <textarea
              v-model="custom_css"
              class="input-primary h-40"
              :placeholder="(changeCase('enter code...', ElementE.PLACEHOLDER) as string)"
              @input="setCustomCss"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Custom design - Text -->
    <div v-if="widget.config" class="tab-wrapper-primary mt-6">
      <div class="tab-heading-primary">{{ changeCase('custom design - text', ElementE.TITLE) }}</div>
      <div class="p-6">
        <div class="flex items-center gap-2">
          <CheckboxSlide
            label=""
            :isChecked="widget.config?.tba_custom_text_enabled ?? false"
            @update:isChecked="toggleTbaCustomText"
          />
          <span class="text-sm font-medium text-gray-900">Replace TBA rate text</span>
        </div>
        <p class="text-xs text-neutral-100 mt-1">
          When enabled, all TBA rate labels on the widget will display your custom text instead.
        </p>
        <div v-if="widget.config?.tba_custom_text_enabled" class="mt-3 flex flex-col gap-4">
          <div>
            <label class="label-primary" for="tba-custom-text">
              TBA rate label
            </label>
            <p class="text-xs text-neutral-100 mb-1">Replaces the "TBA" text shown on unpriced rates.</p>
            <input
              id="tba-custom-text"
              v-model="tbaCustomText"
              class="input-primary block"
              :placeholder="(changeCase('e.g. Closed, Private Event', ElementE.PLACEHOLDER) as string)"
              @input="setTbaCustomText"
            />
          </div>
          <div>
            <label class="label-primary" for="tba-custom-subtext">
              TBA rate subtext
            </label>
            <p class="text-xs text-neutral-100 mb-1">Replaces the "contact us for pricing" text shown below unpriced rates.</p>
            <input
              id="tba-custom-subtext"
              v-model="tbaCustomSubtext"
              class="input-primary block"
              :placeholder="(changeCase('e.g. Please enquire directly', ElementE.PLACEHOLDER) as string)"
              @input="setTbaCustomSubtext"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-else class="h-[600px] w-full text-center" role="status">
      <LoadingIcon />
      <span class="sr-only">Loading...</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { IColorObject, HtmlEvent, WidgetI } from "@/interfaces/interfaces";
import { changeCase } from "../../helpers";
import { ElementE, DropdownTypeE } from '../../enums'
import { iconsList } from '@/components/edittabs/constants';

import SelectColor from "../SelectColor.vue";
import NotificationPopup from "@/components/notifications/NotificationPopup.vue";
import BaseTooltip from '../notifications/BaseTooltip.vue';
import TooltipIcon from "../icons/TooltipIcon.vue";
import DropdownMenu from '@/components/inputs/DropdownMenu.vue';
import LoadingIcon from '@/components/icons/LoadingIcon.vue'
import CheckboxSlide from '@/components/inputs/CheckboxSlide.vue'

export default defineComponent({
  name: "StyleTab",
  props:['widgets'],
  emits:['updateWidget'],
  components: {
    SelectColor,
    NotificationPopup,
    BaseTooltip,
    TooltipIcon,
    DropdownMenu,
    LoadingIcon,
    CheckboxSlide,
  },
  data() {
    return {
      isShowSavePopup: false,
      widget: {} as WidgetI,
      custom_css: '' as string,
      tbaCustomText: '' as string,
      tbaCustomSubtext: '' as string,
      changeCase: changeCase,
      ElementE: ElementE,
      iconsList: iconsList,
      DropdownTypeE,
    };
  },
  mounted() {
    this.widget = this.widgets;
    this.custom_css = this.widget.custom_css
    this.tbaCustomText = this.widget.config?.tba_custom_text ?? ''
    this.tbaCustomSubtext = this.widget.config?.tba_custom_subtext ?? ''
  },
  watch: {
    widgets: function (widgets) {
      this.widget = widgets
      this.custom_css = this.widget.custom_css
      this.tbaCustomText = this.widget.config?.tba_custom_text ?? ''
      this.tbaCustomSubtext = this.widget.config?.tba_custom_subtext ?? ''
    }
  },
  methods: {
    toggleTbaCustomText(value: boolean) {
      this.widget.config.tba_custom_text_enabled = value;
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
    setTbaCustomText() {
      this.widget.config.tba_custom_text = this.tbaCustomText;
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
    setTbaCustomSubtext() {
      this.widget.config.tba_custom_subtext = this.tbaCustomSubtext;
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
    setCustomCss(event: HtmlEvent) {
      let custom_css_string = event.target.value;
      this.widget.custom_css = custom_css_string;
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
    cnangeIcon(e: string) {
      this.widget.config.deal_icon_colour = e;
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
    setColor(colorObject: IColorObject, key: string) {
      const obj = { [key]: colorObject.color };
      this.widget.config = {...this.widget.config, ...obj};
      this.isShowSavePopup = true;
      this.$emit('updateWidget', this.widget);
    },
  },
});
</script>
